import React from "react";
import { MapPin, ArrowDown } from "lucide-react";
import { MAPS_DIRECTIONS_URL } from "@/lib/info";

const HERO_BG =
  "https://images.unsplash.com/photo-1554735361-3e8df0da97f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxkaXZlJTIwYmFyJTIwaW50ZXJpb3IlMjBuZW9ufGVufDB8fHx8MTc4MDAwODEwOHww&ixlib=rb-4.1.0&q=85";

const STATS = [
  { id: "lonestar", k: "$3.50", l: "Lone Star · PBR" },
  { id: "combo", k: "$8", l: "Beer + Shot Combo" },
  { id: "jello", k: "$3", l: "Neon Jell-O Shots" },
  { id: "hh", k: "5h", l: "Happy Hour M–F" },
];

const HeroBackground = () => (
  <>
    <img
      src={HERO_BG}
      alt="Sunny's Bar — neon-lit dive bar interior in downtown Houston with red and amber bar signage"
      className="absolute inset-0 w-full h-full object-cover opacity-60"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#0A0A0A]" />
    <div className="absolute inset-0 bg-black/30 mix-blend-multiply" />
  </>
);

const HeroTitle = () => (
  <h1
    className="font-display leading-[0.88] text-[14vw] md:text-[10vw] lg:text-[8.5vw] tracking-tight rise"
    data-testid="hero-title"
  >
    <span className="block text-[#F5F2E6]">NO FRILLS.</span>
    <span className="block">
      <span className="text-[#F5F2E6]">JUST </span>
      <span className="text-[#FF2A2A] neon-flicker">COLD DRINKS</span>
    </span>
    <span className="block text-stroke">&amp; GOOD TIMES.</span>
  </h1>
);

const HeroCopy = () => (
  <p
    data-testid="hero-subtitle"
    className="md:col-span-7 text-lg md:text-xl text-[#F5F2E6]/85 max-w-2xl leading-relaxed rise"
  >
    Downtown Houston's favorite neighborhood dive since day one. Located right
    at the corner of{" "}
    <span className="text-[#FFB800] font-semibold neon-amber">
      Capitol &amp; Main
    </span>{" "}
    — walking distance from Toyota Center, Minute Maid Park &amp; the
    METRORail.
  </p>
);

const HeroCTAs = () => (
  <div className="md:col-span-5 flex flex-wrap items-center gap-4 rise">
    <a
      href="#menu"
      data-testid="hero-cta-menu"
      className="btn-press inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#ff4242] text-white px-6 py-4 rounded-sm font-mono-plex text-xs uppercase tracking-[0.25em]"
    >
      <ArrowDown size={16} strokeWidth={2.5} />
      See the Menu
    </a>
    <a
      href={MAPS_DIRECTIONS_URL}
      target="_blank"
      rel="noreferrer"
      data-testid="hero-cta-map"
      className="btn-press inline-flex items-center gap-2 border border-[#F5F2E6]/40 hover:border-[#FFB800] hover:text-[#FFB800] text-[#F5F2E6] px-6 py-4 rounded-sm font-mono-plex text-xs uppercase tracking-[0.25em]"
    >
      <MapPin size={16} strokeWidth={2.5} />
      See the Map
    </a>
  </div>
);

const StatStrip = () => (
  <div className="mt-14 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rise">
    {STATS.map((s) => (
      <div
        key={s.id}
        className="bg-[#0A0A0A]/85 backdrop-blur-sm px-6 py-6 md:py-8"
        data-testid={`hero-stat-${s.id}`}
      >
        <div className="font-display text-4xl md:text-5xl text-[#FFB800] leading-none tabular-nums">
          {s.k}
        </div>
        <div className="mt-2 font-mono-plex text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">
          {s.l}
        </div>
      </div>
    ))}
  </div>
);

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex flex-col"
    >
      <HeroBackground />
      <div className="relative z-10 flex-1 flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-16 md:pb-24 pt-28 md:pt-32">
        <div className="flex items-center gap-3 text-[#A3A3A3] font-mono-plex text-[11px] tracking-[0.3em] uppercase mb-6 rise">
          <span className="h-px w-10 bg-[#FF2A2A]" />
          <span>902 Capitol St · Downtown Houston · Open Late</span>
        </div>
        <div
          data-testid="hero-brand"
          className="font-display text-5xl md:text-7xl lg:text-8xl tracking-wide text-[#F5F2E6] leading-none mb-6 rise whitespace-nowrap"
        >
          SUNNY<span className="text-[#FF2A2A] neon-flicker">'</span>S BAR
        </div>
        <HeroTitle />
        <div className="mt-8 md:mt-10 grid md:grid-cols-12 gap-6 md:gap-10 items-end">
          <HeroCopy />
          <HeroCTAs />
        </div>
        <StatStrip />
      </div>
    </section>
  );
};

export default Hero;
